/**
 * Permission map for Dukakit internal roles.
 * Mirrors DukakitInternalPermissionsSeeder so the frontend knows what each role can do
 * without needing the dukakit-guard Spatie permissions in the userDetails payload.
 *
 * Usage:
 *   import { dukakitCan } from '@/utils/dukakitCan';
 *   dukakitCan(this.userDetails, 'dukakit.view-revenue')
 */

const ROLE_PERMISSIONS = {
    'dukakit-super-admin': [
        'dukakit.view-dashboard',
        'dukakit.view-companies',
        'dukakit.impersonate-company',
        'dukakit.suspend-company',
        'dukakit.delete-inventory',
        'dukakit.manage-billing',
        'dukakit.view-revenue',
        'dukakit.view-system-health',
        'dukakit.manage-affiliates',
        'dukakit.view-analytics',
        'dukakit.manage-feedback',
        'dukakit.refresh-cache',
        'dukakit.manage-staff',
        'dukakit.extend-trial',
        'dukakit.waive-billing',
        'dukakit.archive-company',
        'dukakit.delete-company',
        'dukakit.manage-sessions',
        'dukakit.send-notifications',
        'dukakit.manage-notes',
        'dukakit.manage-feature-flags',
        'dukakit.manage-announcements',
        'dukakit.manage-coupons',
        'dukakit.view-audit-log',
        'dukakit.manage-product-library',
    ],
    'dukakit-support': [
        'dukakit.view-dashboard',
        'dukakit.view-companies',
        'dukakit.view-analytics',
        'dukakit.manage-feedback',
        'dukakit.manage-notes',
        'dukakit.send-notifications',
        'dukakit.view-audit-log',
    ],
    'dukakit-finance': [
        'dukakit.view-dashboard',
        'dukakit.view-companies',
        'dukakit.manage-billing',
        'dukakit.view-revenue',
        'dukakit.view-analytics',
        'dukakit.extend-trial',
        'dukakit.waive-billing',
        'dukakit.manage-coupons',
        'dukakit.view-audit-log',
    ],
    'dukakit-analytics': [
        'dukakit.view-dashboard',
        'dukakit.view-companies',
        'dukakit.view-analytics',
        'dukakit.view-audit-log',
    ],
    'dukakit-operations': [
        'dukakit.view-dashboard',
        'dukakit.view-companies',
        'dukakit.impersonate-company',
        'dukakit.suspend-company',
        'dukakit.delete-inventory',
        'dukakit.manage-affiliates',
        'dukakit.manage-feedback',
        'dukakit.view-analytics',
        'dukakit.extend-trial',
        'dukakit.waive-billing',
        'dukakit.archive-company',
        'dukakit.manage-sessions',
        'dukakit.send-notifications',
        'dukakit.manage-notes',
        'dukakit.view-system-health',
        'dukakit.refresh-cache',
        'dukakit.manage-announcements',
        'dukakit.view-audit-log',
        'dukakit.manage-product-library',
        'dukakit.manage-billing',
    ],
};

/**
 * @param {Object} userDetails  Vuex userDetails state
 * @param {string} permission   e.g. 'dukakit.view-revenue'
 * @returns {boolean}
 */
export function dukakitCan(userDetails, permission) {
    if (!userDetails || !userDetails.isDukakit) return false;

    // Find this user's dukakit role (stored under guard_name='dukakit')
    const roles = userDetails.roles || [];
    const dukakitRole = roles.find(r => r.name && r.name.startsWith('dukakit-'));

    // No role assigned yet but isDukakit=true → treat as super-admin
    if (!dukakitRole) return true;

    return (ROLE_PERMISSIONS[dukakitRole.name] || []).includes(permission);
}
